import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import { Trash2, FlaskConical, Logs } from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { executeWorkflow } from "../createPluginApis";
import { handlerStyle, unwrapJsonData } from "../common/commonData";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import CustomSelect from "@components/common/CustomSelect";
import InfoTooltip from "@components/common/InfoTooltip";
import { useQuery } from "react-query";
import { fetchLogMappingFields } from "@pages/plugin/createPluginApi";
import { logSaveCategories } from "@pages/plugin/allData";
import { LS_PRODUCT_TYPE } from "../common/commonData";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import {
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import ResponseViewer from "../common/ResponseViewer";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";
interface SaveLogsNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  logUid?: string;
  logTitle?: string;
  logType?: string;
  logCategory?: string;
  log_ls_product_type?: string[];
  logMappings?: LogMapping[];
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

interface LogMapping {
  id: string;
  fieldKey: string; // The key from source data
  fieldPath: string; // Full path expression like {{$json.data.field}}
  fieldValue: any; // The actual value
  mappedLsField: string; // LinkShadow standard field mapping
  displayName: string; // Display name of the mapped field
  category: string; // Category of the mapped field
  isTime: boolean; // Whether this field is the time field
  enabled: boolean;
  parentId?: string | null; // For nested fields
  isExpanded?: boolean; // For collapsible nested groups
  isParent?: boolean; // Whether this is a parent object/array
  childCount?: number; // Number of child fields
}

interface StdFieldType {
  standard: string;
  id: string;
  label: string;
  value: string;
  display_name?: string;
  category?: string;
  [key: string]: any;
}

const SaveLogsNode: React.FC<NodeProps<SaveLogsNodeData>> = ({ id, data }) => {
  const label = data.label || data.header || "Save Logs";

  // Node-level state (for the workflow node itself)
  const [nodeUid, setNodeUid] = useState(data.uid || "");
  const [nodeTitle, setNodeTitle] = useState(data.title || "");
  const [nodeDescription, setNodeDescription] = useState(
    data.description || "",
  );

  // Log-level state (for the save logs configuration)
  const [logUid, setLogUid] = useState((data as any).logUid || "");
  const [logTitle, setLogTitle] = useState((data as any).logTitle || "");
  const [logCategory, setLogCategory] = useState(
    (data as any).logCategory || "",
  );
  const [logLsProductType, setLogLsProductType] = useState<string[]>(
    (data as any).log_ls_product_type || [],
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isTitleValid, setIsTitleValid] = useState(true);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Log mappings state
  const [logMappings, setLogMappings] = useState<LogMapping[]>(
    data.logMappings || [],
  );

  // Standard fields from API
  const [allStandardFields, setAllStandardFields] = useState<StdFieldType[]>(
    [],
  );

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Ref to track previous outputData and prevent unnecessary updates
  const prevOutputDataRef = useRef<any>(null);

  // Ref to track previous outputData for sync effect (prevent circular dependency)
  const prevSyncOutputDataRef = useRef<any>(null);

  // Fetch log mapping fields
  const { data: logMappingData } = useQuery(
    ["fetchLogMappingFields"],
    ({ signal }) => fetchLogMappingFields(signal),
    {
      enabled: true, // Always fetch, not just when expanded
    },
  );

  // Populate allStandardFields from API data
  useEffect(() => {
    if (logMappingData?.data?.standard) {
      const fields = Object.entries(logMappingData.data.standard).map(
        ([key, value]: [string, any], index: number) => ({
          standard: key,
          value: key,
          display_name: value?.display_name ?? "",
          label: key,
          category: value?.category ?? "",
          ...(typeof value === "object" && value !== null ? value : {}),
          id: (index + 1).toString(),
        }),
      );

      setAllStandardFields(fields);
    }
  }, [logMappingData]);

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Manual refresh function for InputViewer
  const refreshInputData = React.useCallback(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [id, edges, nodes]);

  // Get input data from connected nodes - use stringified comparison to avoid infinite loops
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Sync outputData from node data (updated during workflow/node execution)
  // Use stringified comparison to prevent re-renders when content hasn't changed
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    // Skip if no new data
    if (!newOutputData) {
      // Clear output if explicitly set to null/undefined
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    // Skip if it's the same reference (optimization)
    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    // Deep comparison - only update if stringified content changed
    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      // Content is the same, just update the reference to prevent future comparisons
      prevOutputDataRef.current = newOutputData;
    }
    // Deliberately using raw data reference as dependency so effect runs when data object changes
    // but we do deep comparison inside to prevent unnecessary state updates
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Sync logMappings when data.logMappings changes (e.g., when loading saved workflow)
  useEffect(() => {
    if (
      data.logMappings &&
      Array.isArray(data.logMappings) &&
      data.logMappings.length > 0
    ) {
      setLogMappings(data.logMappings);
    }
  }, [data.logMappings]);

  // Sync log configuration fields when data changes (e.g., when loading saved workflow)
  useEffect(() => {
    if ((data as any).logUid) {
      setLogUid((data as any).logUid);
    }
    if ((data as any).logTitle) {
      setLogTitle((data as any).logTitle);
    }
    if ((data as any).logCategory) {
      setLogCategory((data as any).logCategory);
    }
    if ((data as any).log_ls_product_type) {
      setLogLsProductType((data as any).log_ls_product_type);
    }
  }, [
    (data as any).logUid,
    (data as any).logTitle,
    (data as any).logCategory,
    (data as any).log_ls_product_type,
  ]);

  // Sync data changes with parent
  // Use deep comparison to prevent circular dependency with Effect 2
  useEffect(() => {
    // Deep comparison - only sync if outputData content actually changed
    const currentOutputDataStr = JSON.stringify(outputData);
    const prevOutputDataStr = JSON.stringify(prevSyncOutputDataRef.current);

    // Skip if content hasn't changed (prevents circular dependency)
    if (currentOutputDataStr === prevOutputDataStr) {
      return;
    }

    // Update ref to track this sync
    prevSyncOutputDataRef.current = outputData;

    // Get plugin ID and mesh flow for storedParameters
    const pluginId = sessionStorage.getItem("editPluginId") || "";
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    let requestId = "";
    if (activeMeshFlowStr) {
      try {
        const activeMeshFlow = JSON.parse(activeMeshFlowStr);
        requestId = activeMeshFlow.request_id || "";
      } catch (e) {
        console.error("Failed to parse activeMeshFlow:", e);
      }
    }

    // Build complete storedParameters for when this node is part of another workflow
    const mappingFields = logMappings
      .filter((m) => m.enabled)
      .map((mapping, index) => ({
        fieldId: index + 1,
        rawFieldName: mapping.fieldPath.replace("$json.", ""),
        type: "string",
        mappingLsField: mapping.mappedLsField,
        display_name: mapping.displayName,
        category: mapping.category,
        is_time: mapping.isTime,
      }));

    const storedParameters = {
      logUid: logUid,
      logTitile: logTitle,
      requestId: requestId,
      logCategory: logCategory,
      pluginId: pluginId,
      log_ls_product_type: logLsProductType,
      mappingFields: mappingFields,
      options: {
        batchSize: 100,
        continueOnError: false,
        validateDataTypes: true,
        disableDotNotation: false,
      },
    };

    data.onDataChange?.(id, {
      uid: nodeUid,
      title: nodeTitle,
      description: nodeDescription,
      logUid,
      logTitle,
      logCategory,
      log_ls_product_type: logLsProductType,
      logMappings,
      outputData,
      storedParameters,
    });
  }, [
    nodeUid,
    nodeTitle,
    nodeDescription,
    logUid,
    logTitle,
    logCategory,
    logLsProductType,
    logMappings,
    outputData,
    id,
  ]);

  // Generate unique Node UID on mount (one-time generation)
  useEffect(() => {
    if (!nodeUid) {
      const epoch = Date.now();
      const baseTitle = nodeTitle || "SaveLogsNode";
      const sanitizedTitle = baseTitle
        .replace(/[^a-zA-Z0-9]/g, "_")
        .toLowerCase();
      setNodeUid(`${sanitizedTitle}_${epoch}`);
    }
  }, []);

  // Generate unique Log UID on mount (one-time generation)
  useEffect(() => {
    if (!logUid) {
      const epoch = Date.now();
      const baseTitle = logTitle || "SaveLog";
      const sanitizedTitle = baseTitle
        .replace(/[^a-zA-Z0-9]/g, "_")
        .toLowerCase();
      setLogUid(`${sanitizedTitle}_log_${epoch}`);
    }
  }, []);

  // Generate field mappings from an object structure (auto-populate)
  const generateMappingsFromObject = (
    obj: any,
    prefix: string,
  ): LogMapping[] => {
    const mappings: LogMapping[] = [];
    const timestamp = Date.now();
    let index = 0;

    const processValue = (key: string, value: any, currentPrefix: string) => {
      const fieldPath = currentPrefix ? `${currentPrefix}.${key}` : key;
      const fullPath = `$json.${fieldPath}`;

      if (Array.isArray(value)) {
        // Handle arrays - stringify them
        mappings.push({
          id: `${timestamp}_${index++}`,
          fieldKey: fieldPath, // Use full path as key for nested fields
          fieldPath: fullPath,
          fieldValue: JSON.stringify(value),
          mappedLsField: "",
          displayName: "",
          category: "",
          isTime: false,
          enabled: true,
          parentId: null,
        });
      } else if (typeof value === "object" && value !== null) {
        // Handle nested objects - expand their keys
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          processValue(nestedKey, nestedValue, fieldPath);
        });
      } else {
        // Primitive value
        mappings.push({
          id: `${timestamp}_${index++}`,
          fieldKey: fieldPath, // Use full path as key for nested fields
          fieldPath: fullPath,
          fieldValue: value,
          mappedLsField: "",
          displayName: "",
          category: "",
          isTime: false,
          enabled: true,
          parentId: null,
        });
      }
    };

    Object.entries(obj).forEach(([key, value]) => {
      processValue(key, value, prefix);
    });

    return mappings;
  };

  // Auto-populate field mappings when input data changes
  useEffect(() => {
    if (!inputData || inputData.length === 0) {
      // Don't clear mappings - they might be loaded from saved workflow
      // Only let user manually manage mappings
      return;
    }

    // Get the first input source
    const firstInput = inputData[0];
    const displayData = getDisplayData(firstInput);

    // If mappings already exist (loaded from saved workflow), populate their values
    if (logMappings.length > 0 && displayData) {
      // Extract source data (handle both array and single object)
      let sourceData: any = null;
      if (Array.isArray(displayData) && displayData.length > 0) {
        sourceData = displayData[0]; // Use first item for field values
      } else if (
        typeof displayData === "object" &&
        displayData !== null &&
        !Array.isArray(displayData)
      ) {
        sourceData = displayData;
      }

      if (sourceData) {
        // Update field values in existing mappings
        const updatedMappings = logMappings.map((mapping) => {
          // Skip if already has a value
          if (mapping.fieldValue !== null && mapping.fieldValue !== undefined) {
            return mapping;
          }

          // Extract value based on fieldKey (e.g., "brand.name" -> get brand.name from sourceData)
          const fieldPath = mapping.fieldKey.split(".");
          let value: any = sourceData;

          for (const key of fieldPath) {
            if (value && typeof value === "object" && key in value) {
              value = value[key];
            } else {
              value = null;
              break;
            }
          }

          return {
            ...mapping,
            fieldValue: Array.isArray(value) ? JSON.stringify(value) : value,
          };
        });

        // Only update if values actually changed
        if (JSON.stringify(updatedMappings) !== JSON.stringify(logMappings)) {
          setLogMappings(updatedMappings);
        }
      }
      return;
    }

    // Only auto-populate if mappings are empty
    if (logMappings.length === 0 && displayData) {
      let autoMappings: LogMapping[] = [];

      if (Array.isArray(displayData) && displayData.length > 0) {
        // Array of objects - use first object as template
        const firstItem = displayData[0];
        if (typeof firstItem === "object" && firstItem !== null) {
          autoMappings = generateMappingsFromObject(firstItem, "");
        }
      } else if (
        typeof displayData === "object" &&
        displayData !== null &&
        !Array.isArray(displayData)
      ) {
        // Single object - use it directly
        autoMappings = generateMappingsFromObject(displayData, "");
      }

      if (autoMappings.length > 0) {
        setLogMappings(autoMappings);
      }
    }
  }, [inputData, logMappings]);

  // Handle delete node
  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Remove a mapping
  const removeMapping = (mappingId: string) => {
    setLogMappings(logMappings.filter((m) => m.id !== mappingId));
  };

  // Update mapping field
  const updateMapping = (mappingId: string, updates: Partial<LogMapping>) => {
    setLogMappings(
      logMappings.map((m) => (m.id === mappingId ? { ...m, ...updates } : m)),
    );
  };

  // Handle LinkShadow field selection
  const handleLsFieldChange = (
    mappingId: string,
    selectedValue: string | undefined,
  ) => {
    if (!selectedValue) {
      // Clear the mapping if no value selected
      updateMapping(mappingId, {
        mappedLsField: "",
        displayName: "",
        category: "",
        isTime: false,
      });
      return;
    }

    const selectedField = allStandardFields.find(
      (f) => f.value === selectedValue,
    );

    if (selectedField) {
      updateMapping(mappingId, {
        mappedLsField: selectedValue,
        displayName: selectedField.display_name || selectedField.label,
        category: selectedField.category || "",
      });
    }
  };

  // Handle isTime checkbox
  const handleTimeCheckbox = (mappingId: string, checked: boolean) => {
    if (checked) {
      // Uncheck all other time fields
      setLogMappings(
        logMappings.map((m) =>
          m.id === mappingId ? { ...m, isTime: true } : { ...m, isTime: false },
        ),
      );
    } else {
      updateMapping(mappingId, { isTime: false });
    }
  };

  // Get available fields for dropdown (exclude already selected) - memoized to prevent re-renders
  const getAvailableFields = React.useCallback(
    (currentMappingId: string) => {
      const currentMapping = logMappings.find((m) => m.id === currentMappingId);
      const currentSelected = currentMapping?.mappedLsField;
      const excludedValues = ["Time", "TimeEpoch", "time", "timeepoch"];

      // Filter out already selected fields (except current) and excluded values
      const filtered = allStandardFields.filter(
        (field) =>
          (!logMappings.some(
            (m) => m.id !== currentMappingId && m.mappedLsField === field.value,
          ) ||
            field.value === currentSelected) &&
          !excludedValues.includes(field.value),
      );

      // Map with display_name preference for label (like FieldMappingDialog)
      const result = filtered.map((field) => ({
        ...field,
        label: field.display_name || field.label || field.value,
      }));

      return result;
    },
    [allStandardFields, logMappings],
  );

  // Memoize available fields for each mapping to prevent CustomSelect resets
  const availableFieldsMap = React.useMemo(() => {
    const map: Record<string, any[]> = {};
    logMappings.forEach((mapping) => {
      map[mapping.id] = getAvailableFields(mapping.id);
    });
    return map;
  }, [logMappings, getAvailableFields]);

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: errorResponse
        ? "Error copied to clipboard."
        : "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Build and test the save logs configuration
  const testSaveLogs = async () => {
    // Only include mappings that are enabled and have a mapped LS field
    const enabledMappings = logMappings.filter(
      (m) => m.enabled && m.mappedLsField.trim(),
    );

    if (!inputData?.length) {
      toast({
        variant: "destructive",
        title: "No Input Data",
        description: "Connect a node to provide input data",
      });
      return;
    }

    if (!enabledMappings.length) {
      toast({
        variant: "destructive",
        title: "No Mappings Configured",
        description: "Map at least one field to a LinkShadow standard field",
      });
      return;
    }

    if (!logTitle.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Log Title is required for the save logs node",
        duration: 3000,
      });
      return;
    }

    if (!logCategory.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Log Category is required",
        duration: 3000,
      });
      return;
    }

    if (!logLsProductType || logLsProductType.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "At least one LS Product Type is required",
        duration: 3000,
      });
      return;
    }

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);
      const pluginId = sessionStorage.getItem("editPluginId") || "";

      // Get all nodes and edges from React Flow
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      // Get execution path: all nodes from start to current node in topological order
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending so loading border is visible.
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (!isExecutable) return node;

          return {
            ...node,
            data: {
              ...node.data,
              executionStatus: "pending",
              executionResult: undefined,
              error: undefined,
            },
          };
        }),
      );

      // Build nodes array for workflow - only include nodes in execution path
      const workflowNodes = executableNodes.map((node) => {
        let nodeParameters: any;

        // Determine backend type and sub_type using centralized mapping
        const backendTypeInfo = getNodeBackendType(node.type || "custom");
        let nodeType = backendTypeInfo.type;
        let subType = backendTypeInfo.sub_type;

        // Build parameters based on node type
        if (node.type === "requestBlock") {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        } else if (node.type === "ifElseNode") {
          nodeParameters = {
            conditions: node.data.conditions || {
              options: {
                caseSensitive: true,
                leftValue: "",
                typeValidation: "strict",
                version: 3,
              },
              conditions: [],
              combinator: "and",
            },
            looseTypeValidation: node.data.looseTypeValidation || false,
            options: node.data.options || {},
          };
        } else if (node.type === "splitOutNode") {
          const splitOutFields = (node.data.fieldsToSplitOut?.[0] || "")
            .split(",")
            .map((f: string) => f.trim())
            .filter((f: string) => f.length > 0);

          const nodeOptions = {
            disableDotNotation: node.data.options?.disableDotNotation ?? false,
            destinationFieldName:
              node.data.options?.destinationFieldName?.trim() ?? "",
          };

          nodeParameters = {
            fieldToSplitOut: splitOutFields.join(","),
            include: node.data.include || "noOtherFields",
            options: nodeOptions,
          } as any;

          if (node.data.include === "selectedOtherFields") {
            if (node.data.fieldsToInclude?.[0]) {
              const includeFields = node.data.fieldsToInclude[0]
                .split(",")
                .map((f: string) => f.trim())
                .filter((f: string) => f.length > 0);
              (nodeParameters as any).fieldsToInclude =
                includeFields.length > 0 ? includeFields.join(",") : "";
            } else {
              (nodeParameters as any).fieldsToInclude = "";
            }
          }
        } else if (
          node.type === "editFields" ||
          node.type === "editFieldsNode"
        ) {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        } else if (node.type === "startNode") {
          nodeParameters = {};
        } else if (node.type === "savelogs") {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        } else if (node.type === "savedatalist") {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
        } else if (
          node.type === "authentication" ||
          node.type === "configurationNode" ||
          node.data.configData
        ) {
          // Configuration/authentication nodes - use proper backend structure
          const configData = node.data.configData || {};
          nodeParameters = {
            TabId: configData.tabId || configData.TabId,
            Uid: configData.uid || configData.Uid,
            pluginId: pluginId,
            configType: configData.configType || configData.type || "temp",
            configData: {
              ...configData,
            },
          };
        } else {
          nodeParameters =
            node.data.storedParameters || node.data.parameters || {};
          // Override type/subtype if explicitly set in node data
          if (node.data.sub_type) {
            subType = node.data.sub_type;
            nodeType = node.data.type || "core";
          }
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.header || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters: {
            ...nodeParameters,
            label: node.data.label, // Preserve node type label
          },
        };
      });

      // Create a mapping from React Flow node IDs to UIDs for executable nodes
      const nodeUidMap: Record<string, string> = {};
      executableNodes.forEach((node) => {
        nodeUidMap[node.id] = node.data.uid || node.id;
      });

      // Build connections object (keyed by source node uid)
      // Create Set of node IDs in execution path for filtering
      const executionPathNodeIds = new Set(executableNodes.map((n) => n.id));

      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeUidMap[edge.source];
          const targetUid = nodeUidMap[edge.target];

          // Only include connections between nodes in execution path
          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          // Check if source node is an if_else node
          const sourceNode = executableNodes.find((n) => n.id === edge.source);
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          // For if_else nodes, route to different output arrays based on handle
          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            // true handle → main[0], false handle → main[1]
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      // Prepare workflow payload using centralized utility
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const response = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (response?.results?.[0]?.node_results) {
        const nodeResults = response.results[0].node_results;
        const allEdges = reactFlowInstance.getEdges();

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeUid = nodeUid || id;
        const currentNodeResult = resultsMap.get(currentNodeUid);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const unwrappedCurrentOutput = unwrapJsonData(
              currentNodeResult.output_data,
            );
            setOutputData(unwrappedCurrentOutput);
            setErrorResponse(null);

            // Update edge data with item count
            reactFlowInstance.setEdges((edges) =>
              edges.map((edge) => {
                if (edge.source === id) {
                  return {
                    ...edge,
                    data: {
                      ...edge.data,
                      itemCount: Array.isArray(unwrappedCurrentOutput)
                        ? unwrappedCurrentOutput.length
                        : 0,
                    },
                  };
                }
                return edge;
              }),
            );
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          toast({
            variant: "success",
            title: "Execution Success",
            description: "Save Logs executed successfully!",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        const isSuccess =
          response?.success === true ||
          response?.status === "success" ||
          response?.body?.status === 1 ||
          response?.body?.status === "1" ||
          response?.status === 1;

        if (isSuccess) {
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: response?.message || "Save Logs executed successfully",
            duration: 3000,
          });
        } else {
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      response?.body?.message ||
                      response?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          setOutputData([
            {
              error: true,
              message:
                response?.body?.message ||
                response?.message ||
                "Workflow execution failed",
              details: response,
              execution_id: response.excution_id || response.execution_id,
            },
          ]);

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              response?.body?.message ||
              response?.message ||
              "Save Logs execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Save Logs Execution Error:", error);
      setErrorResponse(
        error?.message || "Failed to execute save logs configuration",
      );
      setOutputData(null);

      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error:
                  error?.message || "Failed to execute save logs configuration",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      setOutputData([
        {
          error: true,
          message:
            error?.message || "Failed to execute save logs configuration",
          details: error?.response?.data || error,
          timestamp: new Date().toISOString(),
        },
      ]);

      toast({
        variant: "destructive",
        title: "Error",
        description:
          error?.message || "Failed to execute save logs configuration",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Extract and merge input data from connected nodes
  const getDisplayData = (input: any) => {
    const EXCLUDE = [
      "uid",
      "isDecript",
      "isDecrypted",
      "title",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
      "label",
      "nodeType",
      "itemCount",
    ];

    // Helper function to extract json_data from backend output format
    const extractJsonData = (data: any) => {
      if (Array.isArray(data)) {
        // If array contains objects with json_data field, extract it
        return data.map((item) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }
      // If single object with json_data field, extract it
      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }
      return data;
    };

    // Priority: outputData > response > configData
    // Check if data is an array and return it directly
    if (input.outputData) {
      const extracted = extractJsonData(input.outputData);
      return extracted;
    }

    if (input.response) {
      const extracted = extractJsonData(input.response);
      return extracted;
    }

    // For configData, create an object
    const displayData: any = {};
    if (input.configData) {
      // Get list of encrypted fields from isDecript array
      const encryptedFields = input.configData.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => {
          // Mask encrypted fields with asterisks
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }
    return displayData;
  };

  // Render input data section
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  // Render value for display
  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined)
      return <span className="text-muted-foreground italic">null</span>;

    if (Array.isArray(value)) {
      return (
        <span className="text-xs text-blue-600">Array[{value.length}]</span>
      );
    }

    if (typeof value === "object") {
      return (
        <span className="text-xs text-purple-600">
          Object ({Object.keys(value).length} keys)
        </span>
      );
    }

    const strValue = String(value);
    return (
      <span className="text-xs">
        {strValue.length > 50 ? strValue.substring(0, 50) + "..." : strValue}
      </span>
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={<Logs />}
          label={label}
          title={nodeTitle}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={nodeDescription}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Node Details Section */}
      {isExpanded && (
        <div className="bg-muted/30 bg-muted flex-shrink-0">
          <div className="p-2 px-4 border-t flex items-start gap-3">
            <div className="w-2/4">
              <label className="text-xs font-medium text-muted-foreground">
                Node Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={nodeTitle}
                onChange={setNodeTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-2/4">
              <label className="text-xs font-medium text-muted-foreground">
                Node Description (optional)
              </label>
              <textarea
                value={nodeDescription}
                onChange={(e) => setNodeDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Log Configuration Section */}
          <div className="pt-4 px-4 border-t space-y-3 flex-shrink-0">
            <div className="flex items-start gap-3">
              {/* Log Title */}
              <div className="space-y-1 w-1/3">
                <label className="text-xs font-medium text-muted-foreground h-4 flex items-center">
                  Log Title <span className="text-primary ml-1">*</span>
                </label>
                <Input
                  value={logTitle}
                  onChange={(e) => setLogTitle(e.target.value)}
                  placeholder="Enter log title"
                  className="text-xs"
                  required
                />
              </div>
              {/* Log Category */}
              <div className="space-y-1 w-1/3">
                <label className="text-xs font-medium text-muted-foreground h-4 flex items-center">
                  Log Category <span className="text-primary ml-1">*</span>
                </label>
                <CustomSelect
                  data={logSaveCategories}
                  placeholder="Select Log Category"
                  onSelect={(value) => setLogCategory(value)}
                  defaultValue={logCategory ? [logCategory] : []}
                  multiSelect={false}
                  sorted={true}
                />
              </div>
              {/* LS Product Type — tooltip sits beside label, same h-4 row height as siblings */}
              <div className="space-y-1 w-1/3">
                <div className="h-4 flex items-center gap-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    LS Product Type <span className="text-primary">*</span>
                  </label>
                  <InfoTooltip text="Select the LinkShadow product types for this log. You can select multiple options." />
                </div>
                <CustomSelect
                  data={LS_PRODUCT_TYPE}
                  placeholder="Select LS Product Type"
                  onSelect={(selected) => setLogLsProductType(selected)}
                  defaultValue={logLsProductType}
                  multiSelect={true}
                />
              </div>
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Field Mappings (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2">
                  <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                    Parameters
                  </h3>

                  {/* Empty State */}
                  {logMappings.length === 0 && (
                    <div className="border-2 border-dashed border-muted-foreground rounded-md p-8 text-center">
                      <Logs
                        size={32}
                        className="mx-auto mb-2 text-muted-foreground"
                      />
                      <p className="text-xs text-muted-foreground">
                        Connect an input node to auto-populate field mappings
                      </p>
                    </div>
                  )}

                  {/* Mappings List */}
                  {logMappings.length > 0 && (
                    <div className="space-y-2 pt-1">
                      {logMappings.map((mapping) => (
                        <div
                          key={mapping.id}
                          className="border shadow-md rounded-md bg-muted/20 space-y-2 p-3"
                        >
                          {/* Field Info */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold truncate text-primary">
                                {mapping.fieldKey}
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-1">
                                Value:{" "}
                                <span className="text-primaryBlue">
                                  {renderValue(mapping.fieldValue)}
                                </span>
                              </div>
                            </div>
                            <Trash2
                              size={14}
                              className="cursor-pointer text-primary hover:text-primary/80 flex-shrink-0"
                              onClick={() => removeMapping(mapping.id)}
                            />
                          </div>

                          {/* LinkShadow Field Mapping */}
                          <div className="space-y-1">
                            <label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
                              LinkShadow Field{" "}
                              <span className="text-primary">*</span>
                            </label>
                            <CustomSelect
                              key={`${mapping.id}-${mapping.mappedLsField}`}
                              data={availableFieldsMap[mapping.id] || []}
                              placeholder="Search or scroll to select field..."
                              defaultValue={
                                mapping.mappedLsField
                                  ? [mapping.mappedLsField]
                                  : []
                              }
                              sorted={true}
                              multiSelect={false}
                              // disableVirtualization={true}
                              onSelect={(selected?: string) =>
                                handleLsFieldChange(mapping.id, selected)
                              }
                            />
                          </div>

                          {/* IsTime Checkbox - only enabled if field is mapped */}
                          {mapping.mappedLsField && (
                            <div className="flex items-center gap-2 pt-2">
                              <input
                                type="checkbox"
                                id={`time-${mapping.id}`}
                                checked={mapping.isTime}
                                onChange={(e) =>
                                  handleTimeCheckbox(
                                    mapping.id,
                                    e.target.checked,
                                  )
                                }
                                className="w-4 h-4 cursor-pointer"
                              />
                              <label
                                htmlFor={`time-${mapping.id}`}
                                className="text-[10px] cursor-pointer flex items-center gap-1 text-muted-foreground"
                              >
                                Is Time Field
                                <InfoTooltip text="Mark this field as the time field. Only one field can be time field." />
                              </label>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Execute Button */}
                <div className="sticky -bottom-1 left-0 right-0 bg-background border-t">
                  <Button
                    onClick={testSaveLogs}
                    disabled={
                      !isTitleValid ||
                      !inputData ||
                      inputData.length === 0 ||
                      !logMappings.some((m) => m.mappedLsField.trim()) ||
                      !logTitle.trim() ||
                      !logCategory.trim() ||
                      !logLsProductType ||
                      logLsProductType.length === 0 ||
                      loading
                    }
                    className="w-full mt-4 sticky bottom-0 left-0 right-0 rounded-b-md"
                    size="sm"
                    title="Execute predecessor nodes and preview data that will be saved"
                  >
                    <FlaskConical size={14} className="mr-2" />
                    Execute step
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Output Preview (4/12 columns) */}
            <div
              style={{ width: `${widths[2]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute save logs to see output preview"
                  loadingMessage="Executing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default SaveLogsNode;
